import {
  stepLocomotion,
  angleOf,
  findPath,
  resolveAgainstArena,
  DATA,
  TICK_DT,
  NET_CONFIG,
  type ClassId,
  type PlayerInput,
  type Vec2,
} from '@gladiators/shared';

export interface Body {
  x: number;
  y: number;
  radius: number;
}

/**
 * Authoritative-style client prediction for the local player.
 *
 * - `advance()` runs one fixed sim step (TICK_DT) using the *same*
 *   `stepLocomotion` + arena/body collision the server runs, records the input
 *   in a pending queue, and returns it to be sent.
 * - `reconcile()` (called on every server patch) resets the predicted state to
 *   the authoritative pose, drops inputs the server has already applied
 *   (`seq <= lastSeq`), and replays the still-pending inputs. Small errors
 *   vanish; a large error (should never happen in normal play) just resolves as
 *   a one-off correction.
 * - Rendering reads `renderX/renderY`, which chase the predicted position, so
 *   any correction is visually smoothed instead of snapping.
 */
interface Pending {
  seq: number;
  moveX: number;
  moveY: number;
  target: Vec2 | null;
  clickTick: boolean;
}

export class LocalController {
  x = 0;
  y = 0;
  vx = 0;
  vy = 0;
  radius = DATA.players.hitboxRadius;
  speed = DATA.players.moveSpeed;
  path: Vec2[] | null = null;
  pathIndex = 0;
  facing = 0;
  moving = false;
  seq = 0;

  renderX = 0;
  renderY = 0;

  /** Nearby bodies (enemies + other players), refreshed by the scene each step. */
  bodies: Body[] = [];

  /**
   * feature 5.1b — the local hero's current server-side crowd control, refreshed
   * by the scene each frame from `state.players.get(me)`. `stunnedByServer`
   * freezes prediction (no click-to-move motion, path dropped) so the hero does
   * not drift forward then snap when the stun lands; `slowMag` (a 0..1 fraction)
   * scales the predicted move speed so the client matches the slowed server.
   * Both are also applied during `reconcile()`'s input replay (same `applyStep`).
   */
  stunnedByServer = false;
  slowMag = 0;

  private pendingTarget: Vec2 | null = null;
  private freshClick = false;
  private queue: Pending[] = [];

  configure(cls: ClassId): void {
    this.radius = DATA.classes[cls].radius;
    this.speed = DATA.players.moveSpeed;
  }

  reset(x: number, y: number): void {
    this.x = this.renderX = x;
    this.y = this.renderY = y;
    this.vx = 0;
    this.vy = 0;
    this.path = null;
    this.pathIndex = 0;
    this.queue.length = 0;
  }

  issueClick(worldX: number, worldY: number): void {
    this.pendingTarget = { x: worldX, y: worldY };
    this.freshClick = true;
    this.replan(this.pendingTarget);
  }

  private replan(target: Vec2): void {
    const p = findPath({ x: this.x, y: this.y }, target);
    this.path = p.length > 0 ? p : null;
    this.pathIndex = 0;
  }

  /** One fixed sim step. Returns the input to send to the server. */
  advance(): PlayerInput {
    this.seq++;
    // Movement is click-to-move only: the keyboard move vector is always zero.
    const input: PlayerInput = {
      seq: this.seq,
      moveX: 0,
      moveY: 0,
      target: this.freshClick ? this.pendingTarget : null,
      clickTick: this.freshClick,
    };
    this.freshClick = false;

    this.applyStep(input);
    this.queue.push(input);
    if (this.queue.length > 240) this.queue.shift();
    return input;
  }

  private applyStep(input: Pending): void {
    if (input.clickTick && input.target) this.replan(input.target);

    // feature 5.1b — mirror the server's crowd-control gate (World.tickMatch):
    // a stunned hero cannot move or path; a slowed hero moves at
    // `baseSpeed * (1 - slow)`. Applied here so it also holds during the
    // reconcile() replay, which re-runs this exact method for each pending input.
    if (this.stunnedByServer) {
      this.path = null;
      this.pathIndex = 0;
      this.vx = 0;
      this.vy = 0;
      this.moving = false;
      this.separate();
      return;
    }

    const baseSpeed = this.speed;
    this.speed = baseSpeed * Math.max(0, 1 - this.slowMag);
    this.moving = stepLocomotion(this, input.moveX, input.moveY, TICK_DT);
    this.speed = baseSpeed;
    this.separate();
    this.facing = angleOf(this.vx, this.vy, this.facing);
  }

  private separate(): void {
    for (const b of this.bodies) {
      const dx = this.x - b.x;
      const dy = this.y - b.y;
      const distSq = dx * dx + dy * dy;
      const min = this.radius + b.radius;
      if (distSq >= min * min || distSq < 1e-6) continue;
      const dist = Math.sqrt(distSq);
      // half penetration — matches the server's pairwise separation
      const push = (min - dist) / 2;
      this.x += (dx / dist) * push;
      this.y += (dy / dist) * push;
    }
    const c = { x: this.x, y: this.y, r: this.radius };
    resolveAgainstArena(c);
    this.x = c.x;
    this.y = c.y;
  }

  reconcile(serverX: number, serverY: number, lastSeq: number): void {
    while (this.queue.length > 0 && this.queue[0]!.seq <= lastSeq) this.queue.shift();

    this.x = serverX;
    this.y = serverY;
    this.vx = 0;
    this.vy = 0;

    for (const input of this.queue) this.applyStep(input);

    const err = Math.hypot(this.renderX - this.x, this.renderY - this.y);
    if (err > NET_CONFIG.RECONCILE_SNAP_DIST) {
      this.renderX = this.x;
      this.renderY = this.y;
    }
  }

  /** Ease the rendered position toward the predicted one. Call once per frame. */
  smooth(dtSeconds: number): void {
    const k = 1 - Math.pow(1 - NET_CONFIG.RECONCILE_EASE, dtSeconds * 60);
    this.renderX += (this.x - this.renderX) * k;
    this.renderY += (this.y - this.renderY) * k;
  }
}
